import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Select,
  Tabs,
  Table,
  Col,
  Row,
  Button,
  Form,
  Modal,
  Timeline,
  Tag,
} from "antd";
import Text from "antd/lib/typography/Text";
import TextArea from "antd/lib/input/TextArea";
import {
  LinkOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

import "./index.css";
import Sidebar from "../../../components/Sidebar";
import Badge from "../../../components/Badge";
import { DateFormat, getApi, saveApi, errorMessage } from "../../../helpers";
import PaymentSidebar from "../../Payments/Sidebar";
import Popup from "../../../components/Popup";
import { StoreContext } from "../../../context";
import { canWrite } from "../../../helpers/permissions";

// const dataSource = [
//   {
//     key: "1",
//     name: "Mike",
//     age: 32,
//     address: "10 Downing Street",
//     status: "Delivered",
//     price: 899,
//   },
//   {
//     key: "2",
//     name: "John",
//     age: 42,
//     address: "10 Downing Street",
//     status: "Confirmed",
//     price: 99,
//   },
// ];

const columns = [
  {
    title: "Product Name",
    dataIndex: "product_name",
    key: "product_name",
    render: (text, object) =>
      object.variant_name ? text + " (" + object.variant_name + ")" : text,
  },
  {
    title: "Qty",
    dataIndex: "quantity",
    key: "quantity",
    width: "45px",
    render: (text, object, index) => <Badge quantity={text} />,
  },
  {
    title: "Price (₹)",
    key: "buying_price",
    dataIndex: "buying_price",
    width: "80px",
    render: (text, object) => (
      <div style={{ textAlign: "right" }}>
        ₹{!text || text === "0.00" ? object.mrp : text}
      </div>
    ),
  },
];

export default function OrderSidebar({
  visible = false,
  onClose,
  order_id = 0,
  refBack = false,
  onUpdate,
}) {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [popup, setPopup] = useState(false);
  const [updateStatus, setUpdateStatus] = useState("");
  const [cancelOrderReason, setCancelOrderReason] = useState("");
  const [paymentSidebar, setPaymentSidebar] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [activeTab, setActiveTab] = useState("items");

  useEffect(() => {
    if (order_id) getOrderById();
  }, []);

  const getOrderById = async () => {
    setLoading(true);
    const res = await getApi({ order_id: order_id }, "getOrderById");
    if (res.data) {
      setData(res.data);
    }
    setLoading(false);
  };

  const focusSidebar = () => {
    setTimeout(() => {
      document.querySelector('.drwr .ant-drawer-content-wrapper')?.focus();
    }, 100);
  };

  async function updateOrderHandler(type = true) {
    // Validate cancel reason for cancel order
    if (type === true && (!cancelOrderReason || cancelOrderReason.trim() === "")) {
      errorMessage("Please provide a reason for cancellation");
      return;
    }

    setPopup(false);
    setUpdateStatus("");
    setLoading(true);
    
    if (type === true) {
      const res = await saveApi({
        order_id: data.order_id,
        cancel_reason: cancelOrderReason,
        cancelled_by: "Seller",
        updated_by: 1,
      });
      
      if (res && res.success) {
        setCancelOrderReason("");
        getOrderById();
        onUpdate?.();
        focusSidebar();
      } else {
        setLoading(false);
      }
    } else if (type === false) {
      // Mark as Paid
      const res = await saveApi(
        { order_id: data.order_id, updated_by: 1 },
        "Order Marked as Paid",
        "Orderstatus"
      );
      if (res) {
        getOrderById();
        onUpdate?.();
        focusSidebar();
      } else {
        setLoading(false);
      }
    } else if (type === "markDelivered") {
      // Mark as Delivered
      const res = await saveApi(
        { order_id: data.order_id, status: "delivered", updated_by: 1 },
        "Order Marked as Delivered",
        "updateOrderStatus"
      );
      if (res) {
        getOrderById();
        onUpdate?.();
        focusSidebar();
      } else {
        setLoading(false);
      }
    }

  }

  const getPaymentById = async () => {
    if (refBack) {
      onClose();
    } else {
      setLoading(true);
      const res = await getApi(
        { payment_id: data.payment_id },
        "getPaymentById"
      );
      if (res.data) {
        setPaymentDetails(res.data);
        setPaymentSidebar(true);
      }
      setLoading(false);
    }
  };

  const deliveredInText = useMemo(() => {
    const logs = Array.isArray(data.OrderLogs) ? data.OrderLogs : [];
    const updatedLogs = logs.filter((l) => l.status === "order_updated");
    if (updatedLogs.length === 0) return null;
    const latestUpdated = updatedLogs.reduce((a, b) =>
      new Date(a.created_at) > new Date(b.created_at) ? a : b
    );
    const deliveredLog = logs.find(
      (l) => l.status === "delivered" || l.status === "DELIVERED"
    );
    if (!deliveredLog) return null;
    const diffMs =
      new Date(deliveredLog.created_at).getTime() -
      new Date(latestUpdated.created_at).getTime();
    if (isNaN(diffMs) || diffMs < 0) return null;
    const totalSecs = Math.floor(diffMs / 1000);
    if (totalSecs < 60) return `${totalSecs} sec${totalSecs !== 1 ? "s" : ""}`;
    const mins = Math.floor(totalSecs / 60);
    if (mins < 60) return `${mins} min${mins !== 1 ? "s" : ""}`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    if (remMins === 0) return `${hrs} hr${hrs !== 1 ? "s" : ""}`;
    return `${hrs} hr${hrs !== 1 ? "s" : ""} ${remMins} min${remMins !== 1 ? "s" : ""}`;
  }, [data.OrderLogs]);

  const isCancelDisabled = useMemo(() => {
    if (data.order_status === "order_cancelled" || data.order_status === "delivered" || data.payment_status === "captured") return true;

    const logs = Array.isArray(data.OrderLogs) ? data.OrderLogs : [];
    const relevant = logs.filter(log => {
      const s = (log.status || "").toLowerCase();
      return s === "order_paid" || s === "paid" || s === "delivered" || s === "order_delivered";
    });
    if (relevant.length === 0) return false;

    // pick latest relevant log
    const latest = relevant.reduce((a, b) =>
      new Date(a.created_at).getTime() > new Date(b.created_at).getTime() ? a : b
    );
    const ts = new Date(latest.created_at).getTime();
    if (isNaN(ts)) return false; // keep cancel enabled if date invalid
    return Date.now() - ts > 24 * 60 * 60 * 1000; // true if older than 24 hours
  }, [data.order_status, data.payment_status, data.OrderLogs]);

  const getOrderStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "order_created":
        return "blue";
      case "order_updated":
        return "blue";
      case "order_unpaid":
        return "orange";
      case "order_paid":
        return "green";
      case "order_failed":
        return "red";
      case "order_cancelled":
        return "red";
      case "partially_packed":
        return "purple";
      case "packed":
        return "purple";
      case "arrived":
        return "cyan";
      case "dispatched":
        return "orange";
      case "delivered":
        return "green";
      case "cancelled":
        return "red";
      default:
        return "default";
    }
  };

  const getOrderStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "order_created":
        return "Created";
      case "order_updated":
        return "Updated";
      case "order_unpaid":
        return "Unpaid";
      case "order_paid":
        return "Paid";
      case "order_failed":
        return "Failed";
      case "order_cancelled":
        return "Cancelled";
      case "partially_packed":
        return "Partially Packed";
      case "packed":
        return "Packed";
      case "arrived":
        return "Arrived";
      case "dispatched":
        return "Dispatched";
      case "delivered":
        return "Delivered";
      case "cancelled":
        return "Cancelled";
      default:
        return status?.charAt(0).toUpperCase() + status?.slice(1) || "N/A";
    }
  };

  return (
    <>

      {paymentSidebar && (
        <PaymentSidebar
          refBack={true}
          visible={paymentSidebar}
          onClose={() => setPaymentSidebar(false)}
          data={paymentDetails}
        />
      )}
      {popup && updateStatus === "markPaid" && (
        <Popup
          saveText="Yes, Mark"
          title="Mark Order as Paid"
          saveHandler={() => updateOrderHandler(false)}
          visible
          width={380}
          cancelHandler={() => setPopup(false)}
        >
          <Text style={{ fontSize: 15, textAlign: "left" }}>
            Are you sure to mark this order as paid ?
          </Text>
        </Popup>

      )
      }
      {popup && updateStatus === "markDelivered" && (
        <Popup
          saveText="Yes, Mark"
          title="Mark Order as Delivered"
          saveHandler={() => updateOrderHandler("markDelivered")}
          visible
          width={380}
          cancelHandler={() => setPopup(false)}
        >
          <Text style={{ fontSize: 15, textAlign: "left" }}>
            Are you sure to mark this order as delivered ?
          </Text>
        </Popup>
      )}
      {popup && updateStatus === "cancel" && (
        <Modal
          visible
          centered
          maskClosable={false}
          title="Order Cancellation"
          cancelText="No"
          okText="Yes, Cancel"
          cancelButtonProps={{ size: "large" }}
          okButtonProps={{ size: "large" }}
          onCancel={() => setPopup(false)}
          onOk={() => updateOrderHandler(true)}
        >
          <div style={{ textAlign: "center", marginBottom: 15 }}>
            <Text style={{ fontSize: 18 }}>
              Are you sure to cancel the order ?
            </Text>
          </div>
          <Form layout="vertical">
            <Form.Item
              hasFeedback
              labelCol={24}
              label="Reason for cancellation"
              required
              rules={[{ required: true, message: 'Please enter cancellation reason' }]}
            >
              <TextArea
                rows={2}
                maxLength={128}
                showCount
                value={cancelOrderReason}
                placeholder="Ex. Customer Address was invalid."
                onChange={(e) => setCancelOrderReason(e.target.value)}
              />
            </Form.Item>
          </Form>
        </Modal>
      )}
      <Sidebar
        loading={loading}
        visible={visible}
        onClose={onClose}
        footer={
          <div style={{ textAlign: "right" }}>
            <Text style={{ fontSize: 16, marginRight: 20 }} type="secondary">
              Quick Action
            </Text>
            {canWrite() && (
              <>
                <Select
                  onChange={(v) => setUpdateStatus(v)}
                  placeholder="Ex. Cancel Order"
                  style={{
                    width: 250,
                    marginRight: 20,
                    textAlign: "left",
                    // marginBottom: 10
                  }}
                >
                  <Select.Option disabled={isCancelDisabled} value="cancel">
                    Cancel Order
                  </Select.Option>
                  <Select.Option
                    disabled={
                      data.payment_status === "captured"
                      || data.order_status === "order_cancelled"
                      // data.order_status !== "created"
                    }
                    value="markPaid"
                  >
                    Mark as Paid
                  </Select.Option>

                  <Select.Option
                    disabled={
                      // data.payment_status === "captured"
                      data.order_status === "delivered"
                      || data.order_status === "order_cancelled"
                    }
                    value="markDelivered"
                  >
                    Mark as Delivered
                  </Select.Option>
                </Select>
                <Button
                  style={{ marginRight: 20 }}
                  disabled={updateStatus === ""}
                  onClick={() => setPopup(true)}
                  type="primary"
                >
                  Update Order
                </Button>
              </>
            )}
          </div>
        }
      >
        <Row>
          {!storeId && (
            <Col className="lblc" xs={24} md={12}>
              <Text style={{ fontSize: 11 }} type="secondary">
                Store Name
              </Text>
              <br />
              <Text>{data.store_name}</Text>
            </Col>
          )}
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Order Id
            </Text>
            <br />
            <Text>{data.order_id}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Payment Id
            </Text>
            <br />
            <Text>
              {data.payment_id ? (
                <a
                  href="#"
                  onClick={getPaymentById}
                  title="View Payment Details"
                >
                  {data.payment_id + "  "}
                  <LinkOutlined />
                </a>
              ) : (
                "-"
              )}
            </Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Customer Name
            </Text>
            <br />
            <Text>{data.name}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Reference Id
            </Text>
            <br />
            <Text>{data.reference_id ? data.reference_id : "-"}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Customer Mobile
            </Text>
            <br />
            <Text>{data.mobile}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Order Date
            </Text>
            <br />
            <Text>{DateFormat(data.created_at)}</Text>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Order Status
            </Text>
            <br />
            <Badge orderStatus={data.order_status}></Badge>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Payment Status
            </Text>
            <br />
            <Badge paymentStatus={data.payment_status}></Badge>
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Order Type
            </Text>
            <br />
            <Text>
              {data.order_type === "COD"
                ? "Cash On Delivery"
                : data.order_type === "ONLINE"
                  ? "Online"
                  : "NA"}
            </Text>
            {/* <Badge paymentStatus={data.order_type}></Badge> */}
          </Col>
          <Col className="lblc" xs={24} md={12}>
            <Text style={{ fontSize: 11 }} type="secondary">
              Delivery Address
            </Text>
            <br />
            {data.delivery_address?.name ? (
              <Text>
                {data.delivery_address?.name} | {data.delivery_address?.phone}
                <br />
                {data.delivery_address?.address1 +
                  ", " +
                  data.delivery_address?.address2}
                <br />
                {data.delivery_address?.city +
                  " - " +
                  data.delivery_address?.pincode +
                  ", " +
                  data.delivery_address?.state}
                <br />
                {"Type : " + data.delivery_address?.address_type}
              </Text>
            ) : (
              <Text> - </Text>
            )}
          </Col>
          {deliveredInText && (
            <Col className="lblc" xs={24} md={12}>
              <Text style={{ fontSize: 11 }} type="secondary">
                Delivered In
              </Text>
              <br />
              <Text strong>{deliveredInText}</Text>
            </Col>
          )}
        </Row>
        {/* Custom Tabs */}
        <div style={{ marginTop: "24px" }}>
          {/* Tab Headers */}
          <div style={{
            display: "flex",
            borderBottom: "1px solid #d9d9d9",
            marginBottom: "16px"
          }}>
            <div
              onClick={() => {
                console.log("Order tab changed to: items");
                console.log("Order data:", data);
                console.log("Order Details:", data.OrderDetails);
                console.log("Order Logs:", data.OrderLogs);
                setActiveTab("items");
              }}
              style={{
                padding: "8px 16px",
                cursor: "pointer",
                borderBottom: activeTab === "items" ? "2px solid #000" : "none",
                color: activeTab === "items" ? "#000" : "#666",
                fontWeight: activeTab === "items" ? "bold" : "normal",
                backgroundColor: activeTab === "items" ? "#f5f5f5" : "transparent",
                borderRadius: activeTab === "items" ? "4px 4px 0 0" : "none",
              }}
            >
              Line Items
            </div>
            <div
              onClick={() => {
                console.log("Order tab changed to: trails");
                console.log("Order data:", data);
                console.log("Order Details:", data.OrderDetails);
                console.log("Order Logs:", data.OrderLogs);
                setActiveTab("trails");
              }}
              style={{
                padding: "8px 16px",
                cursor: "pointer",
                borderBottom: activeTab === "trails" ? "2px solid #000" : "none",
                color: activeTab === "trails" ? "#000" : "#666",
                fontWeight: activeTab === "trails" ? "bold" : "normal",
                backgroundColor: activeTab === "trails" ? "#f5f5f5" : "transparent",
                borderRadius: activeTab === "trails" ? "4px 4px 0 0" : "none",
              }}
            >
              Order Trails
            </div>
          </div>
          {/* Tab Content */}
          {activeTab === "items" && (
            <div>
              <Row>
                <Table
                  size="small"
                  style={{ marginTop: -16, width: "100%" }}
                  columns={columns}
                  dataSource={data.OrderDetails}
                  pagination={false}
                />
                <Col style={{ marginTop: 10, paddingLeft: "1%" }} xs={16}>
                  <Text>Sub Total</Text>
                  <br />
                  <Text style={{ fontSize: 12 }}>Discount</Text>
                  <br />
                  <Text style={{ fontSize: 12 }}>Tax</Text>
                  <br />
                  <Text style={{ fontSize: 12 }}>Delivery Fee</Text>
                  <br />
                  <br />
                  <Text strong style={{ fontSize: 16 }}>
                    Total
                  </Text>
                </Col>
                <Col
                  style={{
                    marginTop: 10,
                    textAlign: "right",
                    paddingRight: "1%",
                  }}
                  xs={8}
                >
                  <Text>₹ {data.sub_total ? data.sub_total : 0}</Text>
                  <br />
                  <Text style={{ fontSize: 12 }}>
                    ₹ {data.discount ? data.discount : 0}
                  </Text>
                  <br />
                  <Text style={{ fontSize: 12 }}>
                    ₹ {data.gst ? data.gst : 0}
                  </Text>
                  <br />
                  <Text style={{ fontSize: 12 }}>₹ {data.delivery_cost}</Text>
                  <br />
                  <br />
                  <Text strong style={{ fontSize: 16 }}>
                    ₹ {data.grand_total ? data.grand_total : 0}
                  </Text>
                </Col>
              </Row>
            </div>
          )}

          {activeTab === "trails" && (
            <div>
              {data.OrderLogs && data.OrderLogs.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className='cnt-nav'>
                    <div className='lst-itms'>
                      <ul className='lst'>
                        {data.OrderLogs.map((log, index) => {
                          let statusText = "";
                          if (log.status === 'order_created') {
                            statusText = 'Order Created';
                          } else if (log.status === 'order_updated') {
                            statusText = 'Order Updated';
                          } else if (log.status === 'order_unpaid') {
                            statusText = 'Payment Pending';
                          } else if (log.status === 'order_paid') {
                            statusText = 'Payment Completed';
                          } else if (log.status === 'order_cancelled') {
                            statusText = 'Order Cancelled';
                          } else if (log.status === 'order_failed') {
                            statusText = 'Payment Failed';
                          } else if (log.status === 'partially_packed') {
                            statusText = 'Partially Packed';
                          } else if (log.status === 'packed') {
                            statusText = 'Order Packed';
                          } else if (log.status === 'ARRIVED') {
                            statusText = 'Order Shipped';
                          } else if (log.status === 'DISPATCHED' || log.status === 'dispatched') {
                            statusText = 'Order Dispatched';
                          } else if (log.status === 'DELIVERED' || log.status === 'delivered') {
                            statusText = 'Order Delivered';
                          } else if (log.status === 'CANCELLED') {
                            statusText = 'Delivery Cancelled';
                          } else {
                            statusText = log.status ? log.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown Status';
                          }

                          return (
                            <li
                              key={index}
                              className={
                                data.OrderLogs.length > index + 1
                                  ? 'itm compltd'
                                  : 'itm activ'
                              }
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                <div style={{ flex: 1 }}>
                                  <Text strong>{statusText}</Text>
                                  <br />
                                  <Text style={{ fontSize: 12, color: '#666' }}>
                                    {DateFormat(log.created_at)}
                                  </Text>
                                  {log.reason && log.reason.trim() !== "" && (
                                    <>
                                      <br />
                                      <Text style={{ fontSize: 11, color: '#999' }}>
                                        Reason: {log.reason}
                                      </Text>
                                    </>
                                  )}
                                  {log.reference_id && (
                                    <>
                                      <br />
                                      <Text style={{ fontSize: 11, color: '#999' }}>
                                        Ref: {log.reference_id}
                                      </Text>
                                    </>
                                  )}
                                </div>
                                <div style={{ marginLeft: '8px', flexShrink: 0 }}>
                                  <Tag
                                    color={getOrderStatusColor(log.status)}
                                    style={{
                                      fontSize: '10px',
                                      padding: '2px 6px',
                                      lineHeight: '1.2',
                                      borderRadius: '4px'
                                    }}
                                  >
                                    {getOrderStatusText(log.status)}
                                  </Tag>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                  <Text>No order history available</Text>
                </div>
              )}
            </div>
          )}
        </div>
      </Sidebar >
    </>
  );
}
